
import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import DocumentsTable from './DocumentsTable';
import reportsApi, { WindowOption } from '../../api/reportsApi';
import documentsApi from '../../api/documentsApi';
import { DocumentMetadata } from '../../api/types';
import { useBusiness } from '../../context/BusinessContext';

// Mock Data for Sections
// Full Sections List matching Backend
const SECTIONS = [
    { id: 'executive_snapshot', label: 'Executive Snapshot' },
    { id: 'cash_flow_capacity', label: 'Cash Flow Capacity (Trailing Window)' },
    { id: 'liquidity_leverage', label: 'Liquidity & Leverage (Snapshot)' },
    { id: 'profitability_cost', label: 'Profitability & Cost Structure (Trailing Window)' },
    { id: 'growth_volatility', label: 'Growth & Volatility' },
    { id: 'cash_health', label: 'Cash Health & Runway' },
    { id: 'forward_projections', label: 'Forward-Looking Projections' },
    { id: 'working_capital', label: 'Working Capital & Conversion' },
    { id: 'concentration_recurrence', label: 'Concentration & Recurrence' },
    { id: 'credit_box', label: 'Credit Box / Approval Criteria' },
    { id: 'borrower_profile', label: 'Borrower Profile & Management Assessment' },
    { id: 'business_metrics', label: 'Business Metrics (P&L & Balance Sheet)' },
    { id: 'conclusion_recommendation', label: 'Conclusion & Recommendation' },
];

const CreditMemo: React.FC = () => {
    const [timeWindow, setTimeWindow] = useState<WindowOption>(3);
    // Default to all sections selected
    const [selectedSections, setSelectedSections] = useState<string[]>(SECTIONS.map(s => s.id));
    const [isGenerating, setIsGenerating] = useState(false);

    // Data State
    const { selected: business } = useBusiness();
    const [documents, setDocuments] = useState<DocumentMetadata[]>([]);

    // Fetch Documents (Read-only list for Credit Memo context)
    useEffect(() => {
        if (!business?.slug) return;
        const fetchDocs = async () => {
            try {
                const docs = await documentsApi.getDocuments(business.slug);
                setDocuments(docs);
            } catch (error) {
                console.error("Error fetching documents:", error);
            }
        };
        fetchDocs();
    }, [business?.slug]);


    const toggleSection = (id: string) => {
        setSelectedSections(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const handleGenerate = async () => {
        if (!business?.slug) {
            alert("Please select a business first.");
            return;
        }

        setIsGenerating(true);
        try {
            // Map selectedSections IDs to Labels
            const sectionLabels = selectedSections.map(id => SECTIONS.find(s => s.id === id)?.label).filter(Boolean) as any[];

            const { blob, filename } = await reportsApi.generateReport(
                business.slug,
                timeWindow,
                sectionLabels
            );

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 1000);

        } catch (error) {
            console.error("Report generation failed", error);
            alert("Failed to generate report.");
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="flex flex-col gap-8 overflow-y-auto scrollbar-hide pr-1 -mr-1 pb-20 h-full">

            {/* Generation Form */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                {/* Time Window */}
                <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-900 mb-3">1. Select a time window</label>
                    <div className="flex gap-3">
                        {[3, 6, 9].map((window) => (
                            <button
                                key={window}
                                onClick={() => setTimeWindow(window as WindowOption)}
                                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${timeWindow === window
                                    ? 'bg-gray-900 text-white border-gray-900'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                {window} months
                            </button>
                        ))}
                    </div>
                </div>

                {/* Section Selection */}
                <div className="mb-8">
                    <label className="block text-sm font-bold text-gray-900 mb-3">3. Select a section</label>
                    <div className="grid grid-cols-2 gap-4">
                        {SECTIONS.map(section => (
                            <label
                                key={section.id}
                                className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${selectedSections.includes(section.id)
                                    ? 'bg-gray-50 border-gray-300'
                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                <span className="text-sm font-medium text-gray-700">{section.label}</span>
                                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedSections.includes(section.id)
                                    ? 'bg-gray-900 border-gray-900'
                                    : 'bg-white border-gray-300'
                                    }`}>
                                    {selectedSections.includes(section.id) && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                                </div>
                                <input
                                    type="checkbox"
                                    className="hidden"
                                    checked={selectedSections.includes(section.id)}
                                    onChange={() => toggleSection(section.id)}
                                />
                            </label>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end">
                    <button
                        onClick={handleGenerate}
                        disabled={isGenerating || !business?.slug}
                        className="bg-gray-900 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-800 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Generating...
                            </>
                        ) : (
                            'Generate & Download'
                        )}
                    </button>
                </div>
            </div>

            {/* Documents List */}
            <DocumentsTable documents={documents} />

        </div>
    );
};

export default CreditMemo;
